/**
 * Dates and money the way Dale HQ writes them. Panama keeps UTC-5 all year, so
 * a "day" here is always the Panama day of an instant, never the day of the
 * browser's own clock. Month and weekday names are spelled out instead of asked
 * from `Intl`, so the words on the screen do not depend on the browser's locale.
 */

const MONTHS = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'] as const;
const MONTHS_SHORT = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'] as const;
const WEEKDAYS = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'] as const;
const WEEKDAYS_SHORT = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'] as const;

const PANAMA_DAY = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Panama', year: 'numeric', month: '2-digit', day: '2-digit' });

const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

/** `YYYY-MM-DD` of the Panama day an instant falls in. */
export function panamaDayKey(date: Date): string {
  return PANAMA_DAY.format(date);
}

/** The month Panama is in, as `YYYY-MM`. */
export function currentMonth(now: Date = new Date()): string {
  return panamaDayKey(now).slice(0, 7);
}

type DayParts = { year: number; month: number; day: number; weekday: number };

function partsOf(day: string): DayParts {
  const year = Number(day.slice(0, 4));
  const month = Number(day.slice(5, 7));
  const dayOfMonth = Number(day.slice(8, 10));
  return { year, month, day: dayOfMonth, weekday: new Date(Date.UTC(year, month - 1, dayOfMonth, 12)).getUTCDay() };
}

/** "Lunes, 26 de octubre". */
export function longDay(day: string): string {
  const parts = partsOf(day);
  return `${capitalize(WEEKDAYS[parts.weekday] ?? '')}, ${parts.day} de ${MONTHS[parts.month - 1] ?? ''}`;
}

/** "31 oct". */
export function shortDay(day: string): string {
  const parts = partsOf(day);
  return `${parts.day} ${MONTHS_SHORT[parts.month - 1] ?? ''}`;
}

/** "sáb, 24 oct", the heading of a day in a list. */
export function weekdayDay(day: string): string {
  const parts = partsOf(day);
  return `${WEEKDAYS_SHORT[parts.weekday] ?? ''}, ${shortDay(day)}`;
}

/** "octubre". */
export function monthName(month: string): string {
  return MONTHS[Number(month.slice(5, 7)) - 1] ?? month;
}

/** "Octubre de 2026". */
export function monthTitle(month: string): string {
  return `${capitalize(monthName(month))} de ${month.slice(0, 4)}`;
}

/** "Octubre 2026", the month in the top bar. */
export function monthChip(month: string): string {
  return `${capitalize(monthName(month))} ${month.slice(0, 4)}`;
}

/** The month `delta` months from `month`, across years. */
export function shiftMonth(month: string, delta: number): string {
  const index = Number(month.slice(0, 4)) * 12 + (Number(month.slice(5, 7)) - 1) + delta;
  return `${Math.floor(index / 12)}-${String((index % 12) + 1).padStart(2, '0')}`;
}

/** `YYYY-MM-DD` of the last day of a `YYYY-MM`. */
export function lastDayOf(month: string): string {
  const last = new Date(Date.UTC(Number(month.slice(0, 4)), Number(month.slice(5, 7)), 0)).getUTCDate();
  return `${month}-${String(last).padStart(2, '0')}`;
}

const MONEY = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', currencyDisplay: 'narrowSymbol' });
const MINUS = '−';

/**
 * Dollars the way the panel states them: "$500", "$1,000", "$283.15". The cents
 * disappear when the amount is whole dollars; `sign` puts a + or a real minus in
 * front, for a list of movements.
 */
export function formatHqMoney(cents: number, { sign = false }: { sign?: boolean } = {}): string {
  const dollars = Math.abs(cents) / 100;
  const text = MONEY.format(dollars).replace(/\.00$/, '');
  if (sign) return `${cents < 0 ? MINUS : '+'}${text}`;
  return cents < 0 ? `${MINUS}${text}` : text;
}
